import router from '../../helpers/router';
import shutter from '../../helpers/shutter';
import {moveMenuIndicator} from '../menu/index';
import storyView from '../storyView/index';
import {gsap} from '../../vendors/gsap';
import {isSmallContent} from '../../helpers/isMobile';
import appData, {ActiveRoutes} from './appData';

const pageType = {
    Chapters: 'chapters',
    Book: 'book',
    Editorial: 'editorial',
    Story: 'story'
};

/**
 * Handles app routing
 *
 * @returns {undefined}
 */
const appRouting = () => {
    let firstLoad = true;
    let resizeTimer;
    let isResizing = false;
    let hasAnimated = false;
    let resizeObserverAttached = false;
    const appNav = document.querySelector('.app-navbar');
    const appMenu = document.querySelector('.app-menu');
    const loadingScreen = document.querySelector('.loading-screen');
    const appSubMenuDataItems = document.querySelectorAll(
        '.app-submenu a[data-action="explore"], .app-submenu a[data-action="timeline"], .app-submenu a[data-action="book"], .app-submenu a[data-action="editorial"]'
    );
    const chaptersMenuItems = document.querySelectorAll(
        '.app-chapters-menu a, .next-chapter-content a[data-action="editorial"]'
    );
    const header = document.querySelector('.header');
    const $mainContainer = document.querySelector('.app-container');
    const landingContainer = document.querySelector('.landing-container');
    const explorerContainer = document.querySelector('.explore-container');
    const timelineContainer = document.querySelector('.timeline-container');
    const storyContainer = document.querySelector('.story-container');
    const editorialContainer = document.querySelector('.editorial');
    const editorialScroller = document.querySelector('.editorial .scroller');
    const exploreBtn = document.querySelector('[data-action=explore]');
    const timelineBtn = document.querySelector('[data-action=timeline]');
    const homeBtn = document.querySelector('[data-action=home]');
    const chaptersBtn = document.querySelector('[data-action=chapters]');
    const bookBtn = document.querySelector('[data-action=book]');

    let editorialModule;
    let exploreModule;
    let timelineModule;
    let landingModule;
    let currentUrl = '';

    if (!appNav) return;

    const ROUTES = {
        Landing: homeBtn.closest('a')?.href,
        Explore: exploreBtn.closest('a')?.href,
        Timeline: timelineBtn.closest('a')?.href,
        Chapters: 'chapters',
        Book: bookBtn?.closest('a')?.href
    };

    const availableRoutes = [ROUTES.Landing, ROUTES.Explore, ROUTES.Timeline];

    /**
     * Highlights the clicked/loaded menu link
     *
     * @param {HTMLElement} el - clicked button/anchor
     * @returns {undefined}
     */
    const highlightMenuItem = el => {
        if (!el) return;
        const navbar = document.querySelector('.app-navbar-list');

        navbar.childNodes.forEach(link => {
            link.firstElementChild.classList.remove('active-menu');
            link.firstElementChild.removeAttribute('aria-current');
        });

        el.classList.add('active-menu');
        el.setAttribute('aria-current', 'page');

        if (firstLoad) {
            const $appNavBar = document.querySelector('.app-navbar');

            gsap.fromTo(
                '.indicator-bar',
                {
                    width: 0,
                    x: el.getBoundingClientRect().x - $appNavBar.offsetLeft
                },
                {
                    opacity: 1,
                    width: el.offsetWidth
                }
            );

            firstLoad = false;
        } else {
            moveMenuIndicator(el);
        }
    };

    /**
     * Returns name of the page to load - explore/timeline/landing
     *
     * @param {string} path - path with locale
     * @returns {string|undefined} - specific path
     */
    const getRouteName = path => {
        if (path.includes(ROUTES.Explore)) {
            return ActiveRoutes.ExploreView;
        }

        if (path.includes(ROUTES.Timeline)) {
            return ActiveRoutes.TimelineView;
        }

        if (path.includes(ROUTES.Landing)) {
            return ActiveRoutes.LandingView;
        }

        if (path.includes(ROUTES.Book)) {
            return ActiveRoutes.BookView;
        }

        if (
            Array.from(chaptersMenuItems).some(el => {
                return path.includes(el.href);
            })
        ) {
            return ActiveRoutes.ChaptersView;
        }

        return '';
    };

    /**
     * Hides all containers to toggle between various views
     *
     */
    const hideAllContainers = () => {
        const path = window.location.href;
        const routeName = getRouteName(path);

        explorerContainer.classList.add('hidden');
        timelineContainer.classList.add('hidden');
        landingContainer.classList.add('hidden');
        editorialContainer.classList.add('hidden');
        storyContainer.classList.add('hidden');

        document.querySelector('.app-zoom')?.classList.add('hidden');

        // If there is an active editorial and the new route is not the same, unmount it
        if (editorialModule && routeName !== ActiveRoutes.EditorialView) {
            editorialModule.default.unmount();
        }
        if (exploreModule && routeName !== ActiveRoutes.ExploreView) {
            exploreModule.default.unmount();
        }

        if (timelineModule && routeName !== ActiveRoutes.TimelineView) {
            timelineModule.default.unmount();
        }
        if (landingModule && routeName !== ActiveRoutes.LandingView) {
            landingModule.default.unmount();
        }

        // Clear content
        landingContainer.replaceChildren();
    };

    /**
     * Get path and data action of a route by name
     *
     * @returns {Object} If found, return the path and data action
     */
    const getDefaultRoute = () => {
        return {
            targetUrl: ROUTES.Timeline,
            dataAction: 'timeline',
            dataId: ''
        };
    };

    /**
     * Checks if the page is story details
     *
     * @returns {string} - story id
     */
    const isIndividualStory = () => {
        const id = document.querySelector('body').getAttribute('data-active-story') ?? '';

        return id;
    };

    /**
     * Call story view init function with active story data
     *
     * @param {Object} activeStoryData - active story data
     * @param {Object} targetEl - clicked individual story html item
     */
    const openStoryView = (activeStoryData, targetEl) => {
        storyView.init(activeStoryData, targetEl);
    };

    /**
     * Handles the view story event and opens the individual record view
     *
     * @param {MouseEvent} event - app menu click event
     */
    const handleViewStoryEvent = event => {
        const id = event?.detail?.id ?? event;
        const updateUrl = event?.detail?.updateUrl || true;

        // Set active story
        const activeStoryData = appData.setActiveStory(id);

        // return if data not found
        if (!activeStoryData) {
            return;
        }

        // setting the referrer
        let referrer = window.location.href;
        if (activeStoryData.url === window.location.href) {
            referrer = document.querySelector('[data-action="timeline"]')?.href ?? referrer;
        }

        if (appData.getActiveRoute() !== ActiveRoutes.StoryView) {
            appData.setStoryViewReferrer(referrer);
            appData.setActiveRoute(ActiveRoutes.StoryView);
        }

        // Update URL and active route
        if (updateUrl === true) {
            router.push(activeStoryData.url, 'story', activeStoryData.id);
        }

        // Reset data attribute used by the story view component. Otherwise, the back button will show an empty view
        storyContainer.setAttribute('data-should-empty', null);

        // Open individual record View
        openStoryView(activeStoryData, event?.detail?.targetEl);

        // @todo: Add load view in and out transitions
    };

    /**
     * Sets the starting state of the chapters menu for each route
     */
    const initChaptersMenu = () => {
        const path = window.location.href;
        const routeName = getRouteName(path);

        if (routeName === ActiveRoutes.TimelineView) {
            window.dispatchEvent(new CustomEvent('250.chapters', {detail: {chaptersMenu: false}}));
        } else if (routeName === ActiveRoutes.ExploreView) {
            const firstTime = !localStorage.getItem('onboarding');
            if (!firstTime) {
                if (isSmallContent()) {
                    // No chapters menu on mobile versions as this will cause clipped content which needs to be avoided for accessiblity reasons
                    window.dispatchEvent(
                        new CustomEvent('250.chapters', {detail: {chaptersMenu: false, toggleToCollapsed: false}})
                    );
                } else {
                    window.dispatchEvent(
                        new CustomEvent('250.chapters', {detail: {chaptersMenu: true, toggleToCollapsed: true}})
                    );
                }
            }
        } else if (routeName === ActiveRoutes.LandingView) {
            window.dispatchEvent(new CustomEvent('250.chapters', {detail: {chaptersMenu: false}}));
        }

        // Update aria-expanded of the chapters menu button
        if (chaptersBtn) {
            chaptersBtn.setAttribute('aria-expanded', 'false');
        }
    };

    /**
     * Close chapter menu on esc key press
     */
    const closeChaptersMenuOnEscKeyPress = () => {
        window.addEventListener('keydown', function (event) {
            if (event.key === 'Escape') {
                initChaptersMenu();
            }
        });
    };

    /**
     * Decides which page to load
     *
     * @returns {undefined}
     */
    const loadPage = () => {
        const path = window.location.href;
        const {setActiveRoute} = appData;
        const routeName = getRouteName(path);

        storyView.onCloseHandler({updateURL: false, newRoute: routeName});

        // Always hide all containers
        hideAllContainers();

        initChaptersMenu();

        closeChaptersMenuOnEscKeyPress();

        // if new route is timeline and are not already on timeline page
        if (routeName === ActiveRoutes.TimelineView) {
            timelineContainer.classList.remove('hidden');

            highlightMenuItem(timelineBtn);
            setActiveRoute(ActiveRoutes.TimelineView);

            // Dynamically import explore view and render
            import('./timelineView')
                .then(timelineView => {
                    timelineModule = timelineView;
                    timelineView.default.render();
                    shutter.hide();
                })
                .catch(error => {
                    console.error(error);
                });

            return;
            // if new route is explore and not already on explore page
        } else if (routeName === ActiveRoutes.ExploreView) {
            explorerContainer.classList.remove('hidden');

            highlightMenuItem(exploreBtn);
            setActiveRoute(ActiveRoutes.ExploreView);

            // Dynamically import explore view and render
            import('./exploreView')
                .then(exploreView => {
                    exploreModule = exploreView;
                    const firstTime = !localStorage.getItem('onboarding');
                    exploreView.default.render(firstTime);
                    shutter.hide();

                    // Load onboarding wizard when accessing explore for the first time
                    if (firstTime && appData.getActiveRoute() === 'exploreView') {
                        import('../onBoarding')
                            .then(onboarding => {
                                onboarding.default.init();
                            })
                            .catch(() => {
                                // Not handling this error
                            });
                    }
                })
                .catch(error => {
                    console.error(error);
                });

            return;
            // if new route is landing and not already on landing page
        } else if (routeName === ActiveRoutes.LandingView) {
            // hide header and chapters menu
            header.classList.add('hidden');
            if ($mainContainer !== null) {
                $mainContainer.classList.add('m-without_header');
            }
            appMenu.classList.add('hidden');

            landingContainer.classList.remove('hidden');

            setActiveRoute(ActiveRoutes.Landing);

            import('./landing')
                .then(landing => {
                    landing.default.render();
                })
                .catch(error => {
                    console.error(error);
                });

            return;
        } else if (routeName === ActiveRoutes.BookView) {
            highlightMenuItem(bookBtn);
        } else if (routeName === ActiveRoutes.ChaptersView) {
            highlightMenuItem(chaptersBtn);
        }

        shutter.hide();
    };

    /**
     * This function checks if route is valid
     *
     * @param {string} path - pathname
     * @param {Array} routeList - optional list of routes/paths
     * @returns {boolean} - valid route returns true
     */
    const isValidRoute = (path, routeList) => {
        if (routeList) {
            return routeList.some(item => path.includes(item));
        }

        return availableRoutes.some(item => path.includes(item));
    };

    /**
     * Preload an editorial
     *
     * @param {string} url The target url to preload
     */
    const preloadEditorial = async url => {
        try {
            fetch(url)
                .then(data => data.text())
                .then(html => {
                    // Create a temporary container to parse the HTML content
                    const tempContainer = document.createElement('div');
                    tempContainer.innerHTML = html;
                })
                .catch(error => console.error(error));
        } catch {
            // Do nothing as it is just preload
        }
    };

    /**
     * Dynamically load the Editorial Component
     *
     * @param {string} dataAction - editorial type to load
     */
    const loadEditorialComponent = dataAction => {
        hideAllContainers();

        editorialContainer.classList.remove('hidden');

        // Load the editorial JS
        import('../editorial')
            .then(editorial => {
                if (dataAction === pageType.Book) {
                    appData.setActiveRoute(ActiveRoutes.BookView);
                    highlightMenuItem(bookBtn);

                    // Start the closing of the chapters menu
                    window.dispatchEvent(new CustomEvent('250.chapters', {detail: {chaptersMenu: false}}));
                } else {
                    appData.setActiveRoute(ActiveRoutes.ChaptersView);
                    highlightMenuItem(chaptersBtn);
                }

                if (shutter.getStatus() === 'open') {
                    // Hide the shutter
                    shutter.hide();
                }

                editorialModule = editorial;
                editorial.default.init();

                // Attach route handler on next chapter
                const $nextChapter = document.querySelector('.next-chapter .content a');
                if ($nextChapter) {
                    $nextChapter.addEventListener('click', event => {
                        event.preventDefault();

                        shutter.show({
                            /**
                             * callback function that runs after animation complete
                             */
                            onComplete: () => {
                                // eslint-disable-next-line no-use-before-define
                                updateUrlAndLoadPage(event);
                            }
                        });
                    });

                    // Preload next chapter as this is most likely the next page
                    const targetUrl = $nextChapter.href;

                    if (targetUrl && targetUrl.length > 0) {
                        const editorialUrl = new URL(targetUrl);

                        // Get the chapter content without header and footer
                        editorialUrl.searchParams.set('format', 'ajax');

                        preloadEditorial(editorialUrl);
                    }
                }
            })
            .catch(error => {
                console.error(error);
            });
    };

    /**
     * Get the HTML from the backend and replace the "editorial" div element with the result
     *
     * @param {string} url - editorial URL to load
     * @param {string} dataAction - editorial type to load
     */
    const loadEditorial = (url, dataAction) => {
        const editorialUrl = new URL(url);

        // Get the chapter content without header and footer
        editorialUrl.searchParams.set('format', 'ajax');

        fetch(editorialUrl)
            .then(data => data.text())
            .then(html => {
                if (editorialScroller) {
                    // Inject the HTML
                    editorialScroller.innerHTML = html;
                    loadEditorialComponent(dataAction);
                }
            })
            .catch(error => console.error(error));
    };

    /**
     * Routes the page their respective loader
     *
     * @param {string} targetUrl - path to update in url
     * @param {string} dataAction The data action of the page
     * @param {string} dataId The id of an object to be passed
     */
    const routeLoader = (targetUrl, dataAction, dataId) => {
        // if somehow app content has hidden class this will make sure to remove it
        document.querySelector('.app-content').classList.remove('hidden');

        if (targetUrl && dataAction === pageType.Story && dataId) {
            hideAllContainers();

            storyView.onCloseHandler({updateURL: false});

            handleViewStoryEvent({
                detail: {
                    id: dataId,
                    updateUrl: false
                }
            });
        } else if (dataAction === pageType.Chapters) {
            const activeRoute = appData.getActiveRoute();
            const toggleToCollapsed = !isSmallContent() && activeRoute === ActiveRoutes.ExploreView;

            // Data action 'chapters' opens the chapters menu
            const detail = {
                chaptersMenu: true,
                chaptersMenuExpand: true,
                toggleToCollapsed
            };

            window.dispatchEvent(new CustomEvent('250.chapters', {detail}));

            // Toggle aria-expanded of the chapters menu button
            if (chaptersBtn) {
                chaptersBtn.ariaExpanded = chaptersBtn.ariaExpanded !== 'true';
            }
        } else if ((targetUrl && dataAction === pageType.Book) || dataAction === pageType.Editorial) {
            // Data action book or editorial will fetch the html and inject it

            // The book page uses the general transition
            if (dataAction === pageType.Book) {
                // shutter animation
                shutter.show({
                    /**
                     * callback function that runs after animation complete
                     */
                    onComplete: () => {
                        // All other editorials have a special transition
                        loadEditorial(targetUrl, dataAction);
                    }
                });
            } else {
                // All other editorials have a special transition
                loadEditorial(targetUrl, dataAction);
            }
        } else if (targetUrl && isValidRoute(targetUrl) && currentUrl !== targetUrl) {
            const path = window.location.href;
            const {getActiveRoute} = appData;
            const activeRoute = getActiveRoute();
            const routeName = getRouteName(path);

            // If we're already on the route, do nothing
            if (activeRoute === routeName) {
                initChaptersMenu();
                return;
            }

            // shutter animation
            shutter.show({
                /**
                 * callback function that runs after animation complete
                 */
                onComplete: () => {
                    // Clear the editorial content
                    if (editorialScroller) {
                        editorialScroller.replaceChildren();
                    }

                    loadPage();
                }
            });
        }

        // Update current url
        if (dataAction === pageType.Chapters) {
            currentUrl = targetUrl;
        }
    };

    /**
     * This function handles navigation link click.
     * It updates the url without reload and updates the page content
     *
     * @param {MouseEvent} event - app menu click event
     * @returns {undefined}
     */
    const updateUrlAndLoadPage = event => {
        event.preventDefault();

        // if page is loading don't do anything
        if (appData.getIsLoading()) return;

        const target = event.target;
        let dataAction = 'empty';
        let targetUrl = null;

        moveMenuIndicator(event?.target?.closest('a') || event?.target?.closest('button'));

        if (target.tagName.toLowerCase() === 'button') {
            if (target.dataset && target.dataset.action) {
                dataAction = target.dataset.action;
            }
        } else {
            dataAction = target.closest('a')?.dataset?.action || 'empty';
            targetUrl = target.closest('a')?.href || '';

            router.push(targetUrl, dataAction);
        }

        routeLoader(targetUrl, dataAction);
    };

    /**
     * Story view close event
     *
     * @param {Object} event Event details
     */
    const handleStoryClose = event => {
        if (event.detail.updateURL === true) {
            let goToRouteName = appData.getStoryViewReferrer();

            // No active explore or timeline? Load it
            if (!exploreModule && !timelineModule) {
                let routeData = getDefaultRoute();
                router.push(routeData.targetUrl, 'story', appData.getActiveStory()?.id);
                routeLoader(routeData.targetUrl, routeData.dataAction, routeData.dataId);
            } else {
                router.push(goToRouteName, 'story', appData.getActiveStory()?.id);
            }
        }
    };

    /**
     * Handles the browser back button
     */
    const handlePopState = () => {
        // Need to restore the menu and header?
        const headerEl = document.querySelector('.header');
        const menuEl = document.querySelector('.app-menu');

        if (headerEl && headerEl.classList.contains('zoom-open')) {
            gsap.fromTo('.header', {y: '-100%'}, {y: 0});
            headerEl.classList.remove('zoom-open');
        }

        // show app menu
        if (menuEl && menuEl.classList.contains('zoom-open')) {
            gsap.fromTo('.app-menu', {y: '100%'}, {y: 0, duration: 0.3});
            menuEl.classList.remove('zoom-open');
        }

        routeLoader(
            window.history.state?.page || window.location.href,
            window.history.state?.action || '',
            window.history.state?.id || ''
        );
    };

    /**
     * This function appends the app navigation to different location depending on mobile/desktop.
     *
     * @returns {undefined}
     */
    const updateNavBar = () => {
        const tabletSize = 1024;
        const fragment = document.createDocumentFragment();
        fragment.appendChild(appNav);
        fragment.appendChild(loadingScreen);

        if (window.innerWidth < tabletSize) {
            appMenu.appendChild(fragment);
        } else {
            header.appendChild(fragment);
        }
    };

    /**
     * Handles the content worker done event
     *
     * @param {Object} event Custom event that contains the content worker result details
     */
    const handleContentWorkerDone = event => {
        if (event && event.detail) {
            if (event.detail.action === 'init') {
                // Data is available, check if a deep link needs to be opened
                const storyId = isIndividualStory();

                if (storyId) {
                    // Use the event to view
                    const viewStoryEvent = new CustomEvent('250.view.story', {
                        bubbles: true,
                        cancelable: false,
                        detail: {
                            id: storyId
                        }
                    });

                    window.dispatchEvent(viewStoryEvent);
                }
            }
        }
    };

    /**
     * App sub menu click needs to close the menu
     *
     * @param {MouseEvent} event - app menu click event
     */
    const handleSubMenuClick = event => {
        updateUrlAndLoadPage(event);

        // Close the menu
        const closeMenuEvent = new Event('250.close.menu');
        window.dispatchEvent(closeMenuEvent);
    };

    /**
     *  Handles chapter menu click
     *
     * @param {MouseEvent} event - chapter menu click event
     */
    const chapterMenuClickHandler = event => {
        const isChapterMenuActive = document.querySelector('.app-chapters-menu')?.classList?.contains('expanded');
        if (!isChapterMenuActive) return;
        updateUrlAndLoadPage(event);
    };

    /**
     * Fade out app container on resize
     */
    const finishResize = () => {
        isResizing = false;
        if (hasAnimated) {
            gsap.to('.app-container', {
                autoAlpha: 0,
                duration: 0.5,
                ease: 'power2.out',
                /**
                 * Reload & reset animation flag
                 */
                onComplete: () => {
                    window.location.reload();
                    hasAnimated = false;
                }
            });
        }
    };

    /**
     * Resize handler
     */
    const handleResize = () => {
        if (!isResizing) {
            isResizing = true;
            if (!hasAnimated) {
                gsap.to('.app-container', {
                    duration: 0.5,
                    autoAlpha: 0.7,
                    filter: 'blur(20px)',
                    ease: 'power2.out'
                });
                hasAnimated = true;
            }
        }

        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(finishResize, 500);
    };

    /**
     * Initial data setup and event listeners
     */
    const init = () => {
        const path = window.location.href;
        const routeName = getRouteName(path);

        updateNavBar();

        // Editorial deep links are printed on load, otherwise they are loaded over Ajax
        const editorialMain = document.querySelector('.editorial .experience-main');
        if (editorialMain && editorialMain.childNodes.length) {
            // Activate the correct route
            if (routeName === ActiveRoutes.BookView) {
                loadEditorialComponent(pageType.Book);
            } else {
                loadEditorialComponent(pageType.Chapters);
            }
        } else {
            loadPage();
        }

        // event listeners
        appNav.addEventListener('click', updateUrlAndLoadPage);
        window.addEventListener('resize', updateNavBar);
        window.addEventListener('250.view.story', handleViewStoryEvent);

        // App sub menu can contain data-action links as well
        if (appSubMenuDataItems && appSubMenuDataItems.length > 0) {
            appSubMenuDataItems.forEach(item => {
                item.addEventListener('click', handleSubMenuClick);
            });
        }

        // Chapter menu items needs to be fetched
        if (chaptersMenuItems && chaptersMenuItems.length > 0) {
            chaptersMenuItems.forEach(item => {
                item.addEventListener('click', chapterMenuClickHandler);
            });
        }

        // listener for content to be ready
        window.addEventListener('content.worker.done', handleContentWorkerDone);

        // Listener to handle the closing of the individual record
        window.addEventListener('250.close.story', handleStoryClose);

        window.onpopstate = handlePopState;

        if (resizeObserverAttached === false) {
            window.addEventListener('resize', handleResize);
            resizeObserverAttached = true;
        }
    };

    return {
        init
    };
};

export default appRouting();
